import { API_BASE } from '../shared/api/client'
import { FetchPolicyError, fetchWithPolicy } from '../shared/api/fetchPolicy'
import type { StorefrontRuntimePayload, StorefrontRuntimeResponse } from './storefront-runtime-contract'
import {
  STOREFRONT_RUNTIME_CACHE_TTL_MS,
  STOREFRONT_RUNTIME_ERROR_TTL_MS,
  STOREFRONT_RUNTIME_TIMEOUT_MS,
} from './storefront-runtime-contract'
import { getStorefrontRuntimeCache, setStorefrontRuntimeCache } from './storefront-runtime-cache'

const toErrorMessage = (error: unknown): string => {
  if (error instanceof FetchPolicyError) {
    if (error.kind === 'timeout') return 'Runtime timeout'
    if (error.kind === 'network' && error.message.toLowerCase().includes('timeout')) {
      return 'Runtime timeout'
    }
    if (error.kind === 'aborted') return 'Runtime request aborted'
    return 'Runtime request unavailable'
  }
  return error instanceof Error ? error.message : 'Unknown runtime error'
}

const parseResponseError = async (response: Response): Promise<string> => {
  const base = `HTTP ${response.status}: ${response.statusText}`
  try {
    const contentType = response.headers.get('content-type') || ''
    if (contentType.includes('application/json')) {
      const payload = (await response.json()) as { error?: unknown; message?: unknown }
      const error =
        typeof payload.error === 'string'
          ? payload.error
          : typeof payload.message === 'string'
          ? payload.message
          : null
      return error ? `${base} - ${error}` : base
    }
    const text = (await response.text()).trim()
    return text ? `${base} - ${text.slice(0, 300)}` : base
  } catch {
    return base
  }
}

export const fetchStorefrontRuntimeHtml = async ({
  payload,
  cacheKey,
}: {
  payload: StorefrontRuntimePayload
  cacheKey: string
}): Promise<{ ok: true; html: string } | { ok: false; reason: string }> => {
  const cached = getStorefrontRuntimeCache(cacheKey)
  if (cached) {
    if (cached.html) return { ok: true, html: cached.html }
    return { ok: false, reason: cached.error || 'runtime_cached_failure' }
  }

  try {
    const response = await fetchWithPolicy({
      request: `${API_BASE}/plugin-runner/render/storefront-block`,
      init: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
      timeoutMs: STOREFRONT_RUNTIME_TIMEOUT_MS,
      retries: 0,
    })

    if (!response.ok) {
      const reason = await parseResponseError(response)
      setStorefrontRuntimeCache(cacheKey, {
        html: null,
        error: reason,
        expiresAt: Date.now() + STOREFRONT_RUNTIME_ERROR_TTL_MS,
      })
      return { ok: false, reason }
    }

    const data = (await response.json()) as StorefrontRuntimeResponse
    if (!data.success || typeof data.data?.html !== 'string') {
      const reason =
        typeof data.error === 'string' && data.error.trim().length > 0
          ? data.error
          : 'Invalid runtime response payload'
      setStorefrontRuntimeCache(cacheKey, {
        html: null,
        error: reason,
        expiresAt: Date.now() + STOREFRONT_RUNTIME_ERROR_TTL_MS,
      })
      return { ok: false, reason }
    }

    setStorefrontRuntimeCache(cacheKey, {
      html: data.data.html,
      error: null,
      expiresAt: Date.now() + STOREFRONT_RUNTIME_CACHE_TTL_MS,
    })
    return { ok: true, html: data.data.html }
  } catch (error) {
    const reason = toErrorMessage(error)
    setStorefrontRuntimeCache(cacheKey, {
      html: null,
      error: reason,
      expiresAt: Date.now() + STOREFRONT_RUNTIME_ERROR_TTL_MS,
    })
    return { ok: false, reason }
  }
}

